import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { DateTime } from 'luxon'
import { OWNER_TIMEZONE, type Client } from '@diana/shared'
import { api, type StudioLead } from '../../lib/api.js'
import { Column, TopTitle } from '../../classroom/Shell.js'
import { ErrorLine, Skeleton, Tabs } from '../../classroom/ui.js'

type Filter = 'new' | 'handled' | 'all'

const LANGUAGE: Record<string, string> = {
  pl: 'strona po polsku',
  uk: 'strona po ukraińsku',
  ru: 'strona po rosyjsku',
  en: 'strona po angielsku',
}
const SOURCE: Record<string, string> = { 'contact-form': 'formularz' }

const when = (iso: string) => DateTime.fromISO(iso).setZone(OWNER_TIMEZONE)

/** In the list: „09:12” today, „wczoraj”, then „16.10” (with the year once it is not this one). */
function shortTime(iso: string): string {
  const t = when(iso)
  const now = DateTime.now().setZone(OWNER_TIMEZONE)
  if (t.hasSame(now, 'day')) return t.toFormat('HH:mm')
  if (t.hasSame(now.minus({ days: 1 }), 'day')) return 'wczoraj'
  return t.hasSame(now, 'year') ? t.toFormat('dd.MM') : t.toFormat('dd.MM.yyyy')
}

/** „Natalia S.” → { firstName: 'Natalia', lastName: 'S.' } */
function splitName(name: string): { firstName: string; lastName: string } {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length <= 1) return { firstName: parts[0] ?? name.trim(), lastName: '' }
  return { firstName: parts.slice(0, -1).join(' '), lastName: parts[parts.length - 1]! }
}

/**
 * Studio → Wiadomości (Studio-Leads.png): everything from the contact form
 * lands here — not in a Google Sheet. The inbox on the left, the open message
 * on the right with three answers: reply by e-mail, make them a student,
 * mark it done.
 *
 * On a phone the two panes take turns: the list, then the message with a
 * way back.
 */
export function LeadsPage() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { data: leads, isPending, isError } = useQuery({ queryKey: ['studio', 'leads'], queryFn: api.studio.leads })
  const clients = useQuery({ queryKey: ['studio', 'clients'], queryFn: api.studio.clients })

  const [filter, setFilter] = useState<Filter>('new')
  const [chosen, setChosen] = useState<string | null>(null)

  const all = useMemo(() => leads ?? [], [leads])
  const list = all.filter((l) => (filter === 'all' ? true : filter === 'new' ? !l.handled : l.handled))
  const newCount = all.filter((l) => !l.handled).length
  const picked = list.find((l) => l.id === chosen)
  /** On a desk the first message opens by itself; on a phone only a tap opens one. */
  const open = picked ?? list[0] ?? null

  const handled = useMutation({
    mutationFn: ({ id, value }: { id: string; value: boolean }) => api.studio.updateLead(id, { handled: value }),
    onSuccess: (_row, { id }) => {
      /* In a tab the message is about to leave, open its neighbour instead. */
      if (filter !== 'all') {
        const i = list.findIndex((l) => l.id === id)
        const next = list[i + 1] ?? list[i - 1]
        setChosen(next ? next.id : null)
      }
      queryClient.invalidateQueries({ queryKey: ['studio', 'leads'] })
      queryClient.invalidateQueries({ queryKey: ['studio', 'dashboard'] })
    },
  })

  const addClient = useMutation({
    mutationFn: (lead: StudioLead) =>
      api.studio.createClient({
        ...splitName(lead.name),
        email: lead.email.trim(),
        phone: '',
        tags: [],
        notes: `Wiadomość ze strony, ${when(lead.createdAt).toFormat('dd.MM.yyyy')}:\n${lead.message}`.slice(0, 4000),
        defaultPriceGrosz: 0,
        active: true,
        color: '',
      }),
    onSuccess: (client) => {
      queryClient.invalidateQueries({ queryKey: ['studio', 'clients'] })
      queryClient.invalidateQueries({ queryKey: ['studio', 'accounting'] })
      navigate(`/studio/klienci/${client.id}`)
    },
  })

  /** Already a student? Then the button opens their card instead of making a twin. */
  const existing = (lead: StudioLead): Client | undefined => {
    const email = lead.email.trim().toLowerCase()
    return email ? clients.data?.find((c) => c.email.trim().toLowerCase() === email) : undefined
  }

  return (
    <Column
      density="studio"
      topOnPhone
      top={
        <>
          <span className="hidden desk:block">
            <TopTitle studio>Wiadomości</TopTitle>
          </span>
          <div className="ml-auto">
            <Tabs
              label="Wiadomości"
              value={filter}
              onChange={(v) => {
                setFilter(v)
                setChosen(null)
              }}
              items={[
                { value: 'new', label: newCount > 0 ? `Nowe · ${newCount}` : 'Nowe' },
                { value: 'handled', label: 'Załatwione' },
                { value: 'all', label: 'Wszystkie' },
              ]}
            />
          </div>
        </>
      }
    >
      {isPending ? (
        <div className="p-6 desk:p-12">
          <Skeleton lines={4} />
        </div>
      ) : isError ? (
        <div className="p-6 desk:p-12">
          <ErrorLine>Nie udało się wczytać wiadomości. Odśwież stronę.</ErrorLine>
        </div>
      ) : list.length === 0 ? (
        <p className="m-0 p-6 text-class-ink-soft desk:p-12">
          {all.length === 0
            ? 'Jeszcze nikt nie napisał.'
            : filter === 'new'
              ? 'Nie masz nowych wiadomości. Wszystko załatwione.'
              : 'Nic tu jeszcze nie ma.'}
        </p>
      ) : (
        <div className="flex flex-col desk:h-full desk:flex-row">
          <ul
            aria-label="Skrzynka"
            className={[
              'm-0 list-none py-2 desk:block desk:w-[360px] desk:shrink-0 desk:overflow-y-auto desk:border-r desk:border-class-line desk:py-4',
              picked ? 'hidden' : 'block',
            ].join(' ')}
          >
            {list.map((lead, i) => {
              const current = open?.id === lead.id
              return (
                <li key={lead.id} className={i > 0 ? 'border-t border-class-line' : ''}>
                  <button
                    type="button"
                    aria-current={current ? 'true' : undefined}
                    onClick={() => setChosen(lead.id)}
                    className={[
                      'flex w-full cursor-pointer flex-col gap-0.5 border-0 px-5 py-3.5 text-left font-class-text text-class-body text-class-ink desk:px-6',
                      current ? 'desk:bg-class-field-soft' : 'bg-transparent hover:bg-class-field-soft/50',
                    ].join(' ')}
                  >
                    <span className="flex items-center gap-2">
                      <span
                        aria-hidden
                        className={['h-1.5 w-1.5 shrink-0 rounded-full', lead.handled ? 'bg-transparent' : 'bg-class-sky-700'].join(' ')}
                      />
                      <span className={['min-w-0 truncate', lead.handled ? 'font-normal' : 'font-semibold'].join(' ')}>
                        {lead.name}
                        {!lead.handled && <span className="sr-only"> (nowa)</span>}
                      </span>
                      <span className="ml-auto shrink-0 text-class-label text-class-ink-faint">{shortTime(lead.createdAt)}</span>
                    </span>
                    <span
                      className={[
                        'truncate pl-3.5 text-class-gloss',
                        lead.handled ? 'text-class-ink-faint' : 'text-class-ink-soft',
                      ].join(' ')}
                    >
                      {preview(lead.message)}
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>

          {open && (
            <article
              className={[
                'min-w-0 flex-1 flex-col gap-7 px-5 py-6 desk:flex desk:overflow-y-auto desk:px-14 desk:py-12',
                picked ? 'flex' : 'hidden',
              ].join(' ')}
            >
              <button type="button" onClick={() => setChosen(null)} className="cl-link self-start text-class-gloss desk:hidden">
                ‹ Wszystkie wiadomości
              </button>

              <header className="flex flex-col gap-1.5">
                <h1 className="cl-heading m-0">{open.name}</h1>
                <p className="m-0 text-class-gloss text-class-ink-faint">
                  <a href={`mailto:${open.email}`} className="font-semibold">
                    {open.email}
                  </a>
                  {' · '}
                  {SOURCE[open.source] ?? open.source}
                  {' · '}
                  {LANGUAGE[open.locale] ?? open.locale}
                  {' · '}
                  {when(open.createdAt).toFormat('dd.MM, HH:mm')}
                  {open.handled && ' · załatwione'}
                </p>
              </header>

              <div className="flex max-w-[600px] flex-col gap-3.5">
                {paragraphs(open.message).map((p, i) => (
                  <p key={i} className="m-0 whitespace-pre-wrap">
                    {p}
                  </p>
                ))}
              </div>

              <div className="flex flex-col gap-3">
                <div className="flex flex-wrap items-center gap-6 border-t border-class-line pt-6">
                  <a href={`mailto:${open.email}`} className="cl-btn cl-btn-primary min-h-11 text-class-gloss">
                    Odpisz e-mailem
                  </a>
                  {(() => {
                    const known = existing(open)
                    return known ? (
                      <button
                        type="button"
                        onClick={() => navigate(`/studio/klienci/${known.id}`)}
                        className="cl-link text-class-gloss"
                      >
                        Otwórz kartę ucznia ›
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => addClient.mutate(open)}
                        disabled={addClient.isPending || clients.isPending}
                        className="cl-link text-class-gloss disabled:cursor-default disabled:opacity-60"
                      >
                        {addClient.isPending ? 'Dodaję…' : 'Dodaj jako ucznia'}
                      </button>
                    )
                  })()}
                  <button
                    type="button"
                    onClick={() => handled.mutate({ id: open.id, value: !open.handled })}
                    disabled={handled.isPending}
                    className="cl-link-quiet text-class-gloss disabled:cursor-default disabled:opacity-60"
                  >
                    {open.handled ? 'Przywróć do nowych' : 'Oznacz jako załatwione'}
                  </button>
                </div>
                {addClient.isError && <ErrorLine>Nie udało się dodać ucznia.</ErrorLine>}
                {handled.isError && <ErrorLine>Nie udało się zapisać.</ErrorLine>}
              </div>
            </article>
          )}
        </div>
      )}
    </Column>
  )
}

/** The message on one line, for the list (CSS cuts it with an ellipsis). */
const preview = (message: string) =>
  message
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
    .join(' ')

const paragraphs = (message: string) =>
  message
    .trim()
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean)
